import React, { useState, useContext, useEffect, useRef } from 'react';
import { FaTimes, FaMapMarkerAlt, FaCheck } from 'react-icons/fa';
import { LoginContext } from '../../Contexts/LoginContext';
import Dropdown from './Dropdown';
import countries from '../../data/countries.json';
import currencies from '../../data/currencies.json';
import { states } from '../../data/constants';
import { getCurrencyForCountry } from '../../data/countryToCurrency';
import axios from 'axios';
import { showToast } from './ShowToast';

// Get flag image URL for a country code (e.g. "US")
export const getFlagUrl = (code) => {
    if (!code || code.length !== 2) return null;
    return `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
};

const LocationCurrencyModal = ({ isOpen, onClose }) => {
    const { user, fetchUserData, currency, updateCurrency } = useContext(LoginContext);
    const [activeTab, setActiveTab] = useState('location'); // 'location' | 'currency'
    const [locationStatus, setLocationStatus] = useState('idle');

    const [addressData, setAddressData] = useState({
        country: '',
        state: '',
        city: '',
        address: '',
        zipCode: ''
    });

    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [currencySearch, setCurrencySearch] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const currencyListRef = useRef(null);
    const [detectedCoords, setDetectedCoords] = useState(null);

    // Auto-scroll to selected currency when currency tab opens
    useEffect(() => {
        if (activeTab === 'currency' && selectedCurrency && currencyListRef.current) {
            const timer = setTimeout(() => {
                const activeItem = currencyListRef.current.querySelector('.lcCurrencyItem.active');
                if (activeItem) {
                    activeItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [activeTab, selectedCurrency]);

    useEffect(() => {
        if (isOpen && user) {
            const ba = user.buyerAddress || {};
            setAddressData({
                country: ba.country?.name ? { value: ba.country.code, label: ba.country.name } : '',
                state: ba.state?.name ? { name: ba.state.name, tax: ba.state.tax } : '',
                city: ba.city || '',
                address: ba.address || '',
                zipCode: ba.zipCode || ''
            });
            setSelectedCurrency(currency || '');
        }
    }, [isOpen, user]);

    const handleInputChange = (field, value) => {
        setAddressData(prev => {
            if (field === 'country') {
                const currCode = getCurrencyForCountry(value.value);
                if (currCode) setSelectedCurrency(currCode);
                return {
                    ...prev,
                    country: value,
                    state: value.label === 'Canada' ? null : { name: '', tax: 5 }
                };
            }
            if (field === 'state' && addressData.country?.label === 'Canada') {
                return { ...prev, state: { name: value.label, tax: value.value } };
            }
            return { ...prev, [field]: value };
        });
    };

    const fetchLocation = () => {
        if (!navigator.geolocation) {
            showToast('Geolocation not supported by your browser', 'error');
            return;
        }
        setLocationStatus('loading');

        const uiTimeout = setTimeout(() => {
            setLocationStatus(prev => prev === 'loading' ? 'timeout' : prev);
        }, 3000);

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                setDetectedCoords({ lat: latitude, lon: longitude });
                try {
                    const res = await fetch(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/geocode?lat=${latitude}&lon=${longitude}`);
                    const data = await res.json();
                    if (data?.address) {
                        const addr = data.address;
                        const countryName = addr.country;
                        const countryMatch = countries.find(c => c.name.toLowerCase() === countryName?.toLowerCase());
                        const stateName = addr.state || addr.region || '';
                        const cityName = addr.city || addr.town || addr.village || '';
                        const road = addr.road || addr.suburb || '';
                        const houseNumber = addr.house_number || '';
                        const fullAddress = [houseNumber, road].filter(Boolean).join(' ');
                        const zipCode = addr.postcode || '';

                        setAddressData(prev => ({
                            ...prev,
                            country: countryMatch ? { value: countryMatch.code, label: countryMatch.name } : prev.country,
                            state: stateName ? (countryMatch?.code === 'CA'
                                ? (() => { const s = states.find(s => s.name.toLowerCase() === stateName.toLowerCase()); return s ? { name: s.name, tax: s.tax } : { name: stateName, tax: 5 }; })()
                                : { name: stateName, tax: 5 }) : prev.state,
                            city: cityName || prev.city,
                            address: fullAddress || prev.address,
                            zipCode: zipCode || prev.zipCode
                        }));

                        if (countryMatch) {
                            const curr = getCurrencyForCountry(countryMatch.code);
                            if (curr) setSelectedCurrency(curr);
                        }
                        clearTimeout(uiTimeout);
                        setLocationStatus('success');
                    } else {
                        clearTimeout(uiTimeout);
                        setLocationStatus('idle');
                    }
                } catch (err) {
                    console.log('Reverse geocoding failed:', err);
                    clearTimeout(uiTimeout);
                    setLocationStatus('idle');
                }
            },
            (error) => {
                console.log('Geolocation denied or failed:', error);
                clearTimeout(uiTimeout);
                setLocationStatus('idle');
                showToast('Could not detect location. Please enter manually.', 'error');
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const handleSave = async () => {
        setIsSaving(true);
        const token = localStorage.getItem('token');
        try {
            const submissionData = {
                country: addressData.country ? { name: addressData.country.label, code: addressData.country.value } : undefined,
                state: addressData.state,
                city: addressData.city,
                address: addressData.address,
                zipCode: addressData.zipCode,
                currency: selectedCurrency,
                location: detectedCoords || undefined
            };
            await axios.post(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/updateUser`,
                submissionData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (selectedCurrency && selectedCurrency !== currency) {
                await updateCurrency(selectedCurrency);
            }
            await fetchUserData();
            showToast('Settings updated successfully!', 'success');
            onClose();
        } catch (err) {
            console.error('Failed to save:', err);
            showToast(err.response?.data?.error || 'Failed to update settings', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const filteredCurrencies = currencies.filter(c =>
        c.label.toLowerCase().includes(currencySearch.toLowerCase()) ||
        c.value.toLowerCase().includes(currencySearch.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <div className="lcModalOverlay" onClick={onClose}>
            <div className="lcModal" onClick={(e) => e.stopPropagation()}>
                <div className="lcModalHeader">
                    <h3>Location & Currency</h3>
                    <button className="lcModalCloseBtn" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="lcModalTabs">
                    <button
                        className={`lcTab ${activeTab === 'location' ? 'active' : ''}`}
                        onClick={() => setActiveTab('location')}
                    >
                        <FaMapMarkerAlt /> Location
                    </button>
                    <button
                        className={`lcTab ${activeTab === 'currency' ? 'active' : ''}`}
                        onClick={() => setActiveTab('currency')}
                    >
                        💱 Currency
                    </button>
                </div>

                <div className="lcModalBody">
                    {activeTab === 'location' && (
                        <div className="lcLocationTab">
                            <button className="lcDetectBtn" onClick={fetchLocation} disabled={locationStatus === 'loading'}>
                                <FaMapMarkerAlt />
                                {locationStatus === 'loading' ? 'Detecting...' : 'Use Current Location'}
                            </button>
                            {locationStatus === 'success' && (
                                <div className="locationIndicator success" style={{ justifyContent: 'center', marginBottom: '8px' }}>
                                    <FaCheck /> Location detected
                                </div>
                            )}
                            {locationStatus === 'timeout' && (
                                <div className="locationIndicator timeout" style={{ justifyContent: 'center', marginBottom: '8px' }}>
                                    <FaMapMarkerAlt /> Still detecting... enter manually
                                </div>
                            )}
                            <div className="lcFormRow">
                                <div className="lcFormCol">
                                    <label className="label">Country</label>
                                    <Dropdown
                                        options={countries.map(c => ({ value: c.code, label: c.name }))}
                                        value={addressData.country}
                                        onChange={(value) => handleInputChange('country', value)}
                                        placeholder='Select Country'
                                    />
                                </div>
                                <div className="lcFormCol">
                                    <label className="label">State/Province</label>
                                    {addressData.country?.label === 'Canada' ?
                                        <Dropdown
                                            options={states.map(s => ({ value: s.tax, label: s.name }))}
                                            value={addressData.state ? { value: addressData.state.tax, label: addressData.state.name } : null}
                                            onChange={(value) => handleInputChange('state', value)}
                                            placeholder='Select State'
                                        /> :
                                        <input
                                            type="text"
                                            value={addressData.state?.name || ''}
                                            onChange={(e) => setAddressData(prev => ({ ...prev, state: { name: e.target.value, tax: 5 } }))}
                                            className='inputField'
                                            placeholder="State/Province"
                                        />
                                    }
                                </div>
                            </div>
                            <div className="lcFormRow">
                                <div className="lcFormCol">
                                    <label className="label">City</label>
                                    <input
                                        type="text"
                                        value={addressData.city}
                                        onChange={(e) => setAddressData(prev => ({ ...prev, city: e.target.value }))}
                                        className='inputField'
                                        placeholder="City"
                                    />
                                </div>
                                <div className="lcFormCol">
                                    <label className="label">Zip/Postal Code</label>
                                    <input
                                        type="text"
                                        value={addressData.zipCode}
                                        onChange={(e) => setAddressData(prev => ({ ...prev, zipCode: e.target.value }))}
                                        className='inputField'
                                        placeholder="Zip/Postal Code"
                                    />
                                </div>
                            </div>
                            <div className="lcFormCol" style={{ width: '100%' }}>
                                <label className="label">Address</label>
                                <input
                                    type="text"
                                    value={addressData.address}
                                    onChange={(e) => setAddressData(prev => ({ ...prev, address: e.target.value }))}
                                    className='inputField'
                                    placeholder="Street address"
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'currency' && (
                        <div className="lcCurrencyTab">
                            {selectedCurrency && (() => {
                                const curr = currencies.find(c => c.value === selectedCurrency);
                                return curr ? (
                                    <div className="lcSelectedBanner">
                                        <span className="lcSelectedSymbol">{curr.symbol}</span>
                                        <span className="lcSelectedLabel">{curr.label}</span>
                                        <FaCheck className="lcSelectedCheck" />
                                    </div>
                                ) : null;
                            })()}
                            <input
                                type="text"
                                className="inputField lcCurrencySearch"
                                placeholder="Search currencies..."
                                value={currencySearch}
                                onChange={(e) => setCurrencySearch(e.target.value)}
                            />
                            <div className="lcCurrencyList" ref={currencyListRef}>
                                {filteredCurrencies.map(c => (
                                    <div
                                        key={c.value}
                                        className={`lcCurrencyItem ${selectedCurrency === c.value ? 'active' : ''}`}
                                        onClick={() => setSelectedCurrency(c.value)}
                                    >
                                        <span className="lcCurrencySymbol">{c.symbol}</span>
                                        <span className="lcCurrencyLabel">{c.label}</span>
                                        {selectedCurrency === c.value && <FaCheck className="lcCurrencyCheck" />}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="lcModalFooter">
                    <button className="secondaryBtn" onClick={onClose}>Cancel</button>
                    <button className="primaryBtn" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LocationCurrencyModal;
